import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { FaSignOutAlt, FaChevronDown, FaClock, FaChartPie } from 'react-icons/fa';
import './Header.css';

const formatCredit = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed.toFixed(2) : '0.00';
};

const Header = () => {
  const [username, setUsername] = useState('');
  const [creditInfo, setCreditInfo] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef(null);

  useEffect(() => {
    const fetchUsername = async () => {
      try {
        const response = await fetch('/api/v1/username');
        const data = await response.json();
        setUsername(data.username);
      } catch (error) {
        console.error('Error fetching username:', error);
      }
    };

    fetchUsername();
  }, []);

  const fetchCredits = async () => {
    try {
      const response = await fetch('/api/v1/credits');
      const data = await response.json();
      
      if (data.expires_at && data.expires_at !== 'None' && data.expires_at !== '') {
        setCreditInfo({
          totalUsedCredit: data.total_used_credit,
          totalCredits: data.total_credits,
          slaCompensate: data.sla_compensate,
          expiresAt: data.expires_at,
          subStartTime: data.sub_start_time
        });
      } else {
        setCreditInfo(null);
      }
    } catch (error) {
      console.error('Error fetching credits:', error);
      setCreditInfo(null);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleMenu = (e) => {
    e.stopPropagation();
    if (!showMenu) {
      fetchCredits();
    }
    setShowMenu(!showMenu);
  };

  const grossUsage = Number(creditInfo?.totalUsedCredit) || 0;
  const slaCompensation = Math.max(Number(creditInfo?.slaCompensate) || 0, 0);
  const hasSlaCompensation = slaCompensation > 0;
  const netUsage = Math.max(grossUsage - slaCompensation, 0);

  return (
    <header className="header">
      <div className="logo">
        <span className="logo-1">Ne</span>
        <span className="logo-2">X</span>
        <span className="logo-3">t</span>
        <span className="logo-4">Banker</span>
      </div>
      <div className="header-right">
        <nav>
          <a href="/" className={location.pathname === '/' ? 'active' : ''}>Home</a>
          <a href="/reports" className={location.pathname === '/reports' ? 'active' : ''}>Reports</a>
        </nav>
        {username && <div className="separator"></div>}
        {username && (
          <div className="user-profile" onClick={toggleMenu} ref={dropdownRef}>
            <div className="user-trigger">
              <span className="username-text">{username}</span>
              <FaChevronDown className={`chevron-icon ${showMenu ? 'rotate' : ''}`} />
            </div>
            {showMenu && (
              <div className="dropdown-menu">
                <div className="dropdown-header">
                  <span className="dropdown-username">Hi, {username}</span>
                </div>
                {creditInfo && (
                  <div className="credit-info-container">
                    <div className="usage-summary-card">
                      <div className="usage-summary-heading">
                        <FaChartPie className="credit-icon-small usage-icon" />
                        <div className="usage-title-block">
                          <span className="usage-title">
                            {hasSlaCompensation ? 'Usage after SLA credit' : 'Usage this cycle'}
                          </span>
                          <span className="usage-caption">
                            {hasSlaCompensation ? 'Net usage = task usage − SLA credit' : 'Total task usage'}
                          </span>
                        </div>
                      </div>
                      {hasSlaCompensation ? (
                        <>
                          <div className="usage-equation-row">
                            <span className="usage-amount usage-amount--net">{formatCredit(netUsage)}</span>
                            <span className="usage-equation-symbol">=</span>
                            <span className="usage-equation-value">{formatCredit(grossUsage)}</span>
                            <span className="usage-equation-symbol">−</span>
                            <span className="usage-equation-value usage-equation-value--credit">
                              {formatCredit(slaCompensation)}
                              <sup className="usage-credit-marker">*</sup>
                            </span>
                            <span className="usage-unit">credits</span>
                          </div>
                          <span className="usage-credit-origin-note">
                            * From the previous billing cycle.
                          </span>
                        </>
                      ) : (
                        <div className="usage-amount-row">
                          <span className="usage-amount">{formatCredit(grossUsage)}</span>
                          <span className="usage-unit">credits</span>
                        </div>
                      )}
                      </div>

                    <div className="credit-info-item billing-cycle-item">
                      <div className="credit-label-row">
                        <FaClock className="credit-icon-small" />
                        <span className="credit-label">Billing Cycle</span>
                        <span className="credit-timezone">
                          {(() => {
                            try {
                              const offset = -new Date().getTimezoneOffset();
                              const sign = offset >= 0 ? '+' : '-';
                              const hours = Math.floor(Math.abs(offset) / 60);
                              return `(GMT${sign}${hours})`;
                            } catch (e) {
                              return '';
                            }
                          })()}
                        </span>
                      </div>
                      <div className="credit-value-row">
                        <div className="credit-dates-container">
                          {(() => {
                            const formatDate = (ts) => {
                              const date = new Date(parseInt(ts) * 1000);
                              const y = date.getFullYear();
                              const m = String(date.getMonth() + 1).padStart(2, '0');
                              const d = String(date.getDate()).padStart(2, '0');
                              const h = String(date.getHours()).padStart(2, '0');
                              const min = String(date.getMinutes()).padStart(2, '0');
                              return `${y}/${m}/${d} ${h}:${min}`;
                            };
                            
                            return (
                              <>
                                <div className="credit-date-row">
                                  <span className="date-tag start">FROM</span>
                                  <span className="credit-date-text">{formatDate(creditInfo.subStartTime)}</span>
                                </div>
                                <div className="credit-date-row">
                                  <span className="date-tag end">TO</span>
                                  <span className="credit-date-text">{formatDate(creditInfo.expiresAt)}</span>
                                </div>
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                <a href="/api/v1/logout" className="dropdown-item-link">
                  <FaSignOutAlt className="menu-icon" />
                  <span>Logout</span>
                </a>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
